import React from "react";
import { Shield, FileCheck2, Landmark, Users, Receipt } from "lucide-react";
import Eyebrow from "@/components/Eyebrow";
import { FadeUp } from "@/components/Motion";
import PageHeader from "@/components/PageHeader";
import ThreeWayReconciliation from "@/components/ThreeWayReconciliation";
import CTABlock from "@/components/CTABlock";

const handles = [
  { icon: Landmark, title: "IOLTA account tracking", body: "Dedicated ledger per trust account. Interest posting handled correctly." },
  { icon: Users, title: "Client ledger management", body: "Every client matter has its own sub-ledger. Always." },
  { icon: Receipt, title: "Disbursement tracking", body: "Every disbursement tied to source, approval, and supporting doc." },
  { icon: FileCheck2, title: "3-way reconciliation", body: "The required standard, performed monthly and documented." },
  { icon: Shield, title: "Audit-ready documentation", body: "Organized trails that hold up to bar review without last-minute panic." },
];

export default function TrustCompliance() {
  return (
    <div data-testid="trust-compliance-page">
      <PageHeader
        eyebrow="Trust · Compliance"
        title={<>Trust Accounting <em className="text-[#BA1A1A] not-italic">Done Right</em>, Because It Has To Be.</>}
        lead="When you're managing client funds, bookkeeping isn't just operational, it's regulatory. Mistakes don't just cause confusion. They create risk."
        image={{
          src: "https://images.pexels.com/photos/7876087/pexels-photo-7876087.jpeg",
          alt: "Lady Justice close-up",
        }}
        meta={[
          { label: "Reconciliation", value: "3-Way" },
          { label: "Cadence", value: "Monthly" },
          { label: "Standard", value: "IOLTA" },
        ]}
      />

      {/* WHAT WE HANDLE */}
      <section className="container-editorial py-24 md:py-32" data-testid="handles-section">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-5">
            <Eyebrow>What We Handle</Eyebrow>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-6">
              The full compliance perimeter.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-6">
            <p className="text-lg text-[#0C1D36]/75 max-w-xl dropcap">
              Trust accounting failures rarely come from bad intent, they come from
              systems that weren't built for fiduciary precision. We install the system
              your firm should have had from day one.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#0C1D36]/12">
          {handles.map((h, i) => {
            const Icon = h.icon;
            return (
              <FadeUp key={h.title} className="border-r border-b border-[#0C1D36]/12 p-8 md:p-10 bg-white" data-testid={`handle-${i}`}>
                <Icon className="w-7 h-7 text-[#BA1A1A]" />
                <h3 className="font-serif text-2xl text-[#0C1D36] mt-6 leading-tight">{h.title}</h3>
                <p className="text-[15px] text-[#4B5563] mt-4 leading-relaxed">{h.body}</p>
              </FadeUp>
            );
          })}
        </div>
      </section>

      {/* 3-WAY KEY SELLING POINT */}
      <section className="bg-[#0C1D36] text-[#FDFBF7] noise" data-testid="three-way-feature">
        <div className="container-editorial py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-6">
                This is where most firms get exposed.
              </h2>
            </div>
            <div className="md:col-span-7 md:pt-6">
              <p className="text-lg text-white/75 max-w-xl">
                We perform 3-way reconciliation every month. Bank balance. Book balance.
                Individual client ledgers. <span className="text-white">All three must match.</span>
              </p>
              <p className="mt-4 text-white/60 max-w-xl">
                If they don't, you have a problem. We make sure they do.
              </p>
            </div>
          </div>

          <div className="bg-[#FDFBF7] text-[#0C1D36] p-6 md:p-12 border border-white/10">
            <ThreeWayReconciliation />
          </div>
        </div>
      </section>

      <CTABlock
        eyebrow="Review"
        title="Want us to review how your trust accounting is being handled today?"
        body="We'll walk through your current process and flag exposure points, with no obligation."
        ctaLabel="Review Your Trust Accounting Process"
      />
    </div>
  );
}
